import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { errorMessage, unwrapEnvelope } from 'components/legal/legalUtils';
import { CONSENT_COPY, ConsentPlatformKey, ConsentToggle, LegalConsentSettings } from 'types/legal';

type ToggleKey = keyof LegalConsentSettings;

const ROWS: Array<{ key: ToggleKey; label: string; copy: string }> = [
  { key: 'privacyTerms', label: 'Privacy Policy & Terms', copy: 'I agree to the Privacy Policy and Terms & Conditions' },
  { key: 'sms', label: 'SMS notifications', copy: CONSENT_COPY.sms }
];

const PLATFORMS: Array<{ key: ConsentPlatformKey; label: string }> = [
  { key: 'app', label: 'App' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'weblink', label: 'Weblink' }
];

const normalizeToggle = (toggle?: Partial<ConsentToggle> | null): ConsentToggle => ({
  enabled: toggle?.enabled === true,
  platforms: {
    app: toggle?.platforms?.app === true,
    dashboard: toggle?.platforms?.dashboard === true,
    weblink: toggle?.platforms?.weblink === true
  }
});

export const normalizeConsentSettings = (consent?: Partial<LegalConsentSettings> | null): LegalConsentSettings => ({
  privacyTerms: normalizeToggle(consent?.privacyTerms),
  sms: normalizeToggle(consent?.sms)
});

interface ConsentSettingsProps {
  clientUid: string;
  consent: LegalConsentSettings;
  // Both of this client's documents are published. There are no defaults, so the
  // Privacy/Terms checkbox can't be switched on before that.
  documentsPublished: boolean;
  onSaved: () => void;
}

// The login-checkbox grid. Platform boxes keep their values while a row is off, so
// switching a row back on restores the previous platforms.
const ConsentSettings = ({ clientUid, consent, documentsPublished, onSaved }: ConsentSettingsProps) => {
  const { updateClientLegalConsent, showToast } = useApi();
  const [draft, setDraft] = useState<LegalConsentSettings>(() => normalizeConsentSettings(consent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset only when the saved values change, not on every overview refresh: the usual
  // path after a "publish documents first" 400 is publish → come back → Save, and the
  // refresh that follows a publish must not throw away the unsaved ticks.
  const savedKey = useMemo(() => JSON.stringify(normalizeConsentSettings(consent)), [consent]);

  useEffect(() => {
    setDraft(JSON.parse(savedKey));
    setError(null);
  }, [savedKey]);

  const dirty = JSON.stringify(draft) !== savedKey;

  const setEnabled = (key: ToggleKey, enabled: boolean) => setDraft((prev) => ({ ...prev, [key]: { ...prev[key], enabled } }));

  const setPlatform = (key: ToggleKey, platform: ConsentPlatformKey, value: boolean) =>
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], platforms: { ...prev[key].platforms, [platform]: value } } }));

  const enabledWithoutPlatform = ROWS.filter(({ key }) => draft[key].enabled && !PLATFORMS.some((p) => draft[key].platforms[p.key]));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      unwrapEnvelope(await updateClientLegalConsent(clientUid, draft));
      showToast('Login checkboxes saved', 'success');
      onSaved();
    } catch (err: any) {
      // e.g. 400 "Publish a Privacy Policy and Terms & Conditions before enabling this checkbox."
      const message = errorMessage(err, 'Could not save the login checkboxes.');
      setError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainCard title="Login checkboxes" subheader="Shown on the login page before sign-in, per platform.">
      <TableContainer>
        <Table size="small" aria-label="Login checkboxes per platform">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40%' }}>Checkbox</TableCell>
              <TableCell align="center">Enabled</TableCell>
              {PLATFORMS.map((platform) => (
                <TableCell key={platform.key} align="center">
                  {platform.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ROWS.map((row) => {
              const toggle = draft[row.key];
              const needsDocuments = row.key === 'privacyTerms' && !documentsPublished && !toggle.enabled;
              return (
                <TableRow key={row.key}>
                  <TableCell>
                    <Typography variant="subtitle1">{row.label}</Typography>
                    <Typography variant="body2" color="text.secondary" data-no-translate="true">
                      “{row.copy}”
                    </Typography>
                    {needsDocuments && (
                      <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
                        Publish this client's Privacy Policy and Terms &amp; Conditions below first.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={toggle.enabled}
                      disabled={needsDocuments}
                      onChange={(event) => setEnabled(row.key, event.target.checked)}
                      inputProps={{ 'aria-label': `${row.label} enabled` }}
                    />
                  </TableCell>
                  {PLATFORMS.map((platform) => (
                    <TableCell key={platform.key} align="center">
                      <Checkbox
                        checked={toggle.platforms[platform.key]}
                        disabled={!toggle.enabled}
                        onChange={(event) => setPlatform(row.key, platform.key, event.target.checked)}
                        inputProps={{ 'aria-label': `${row.label}: show on ${platform.label}` }}
                        sx={{ p: 1.25 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {enabledWithoutPlatform.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {enabledWithoutPlatform.map((row) => row.label).join(' and ')} {enabledWithoutPlatform.length > 1 ? 'are' : 'is'} enabled but not
          shown on any platform.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} role="alert">
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Privacy Policy &amp; Terms must be ticked to sign in. SMS is optional: people who leave it unticked still sign in, but aren't
          texted. Nothing is shown until a box is enabled.
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!dirty || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: 'secondary.dark', '&:hover': { bgcolor: 'secondary.main' } }}
          >
            Save
          </Button>
        </Box>
      </Stack>
    </MainCard>
  );
};

export default ConsentSettings;
